import React from "react";
import { NavLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ContactCTA from "../components/ContactCTA";
import FaqSection from "../components/FaqSection";
import CompanyLogo from "../components/CompanyLogo";
import { PhosphorIcon } from "../components/PhosphorIcon";
import { ECOSYSTEM_PARTNERS, canonicalFor } from "../config/site";
import useReveal from "../hooks/useReveal";
import "../styles/partners-page.css";

const ERP_PLATFORMS = [
  { name: "Oracle", domain: "oracle.com" },
  { name: "SAP", domain: "sap.com" },
  { name: "Zoho", domain: "zoho.com" },
  { name: "Microsoft Dynamics", domain: "microsoft.com" },
  { name: "Odoo", domain: "odoo.com" },
  { name: "NetSuite", domain: "netsuite.com" },
];

const PILLARS = [
  {
    icon: "ArrowsLeftRight",
    title: "Vendor-neutral by design",
    body: "We are not tied to any single GSP, ASP or PDP. We recommend and integrate whichever provider fits your mandate, geography and existing stack.",
  },
  {
    icon: "PlugsConnected",
    title: "Integration, not lock-in",
    body: "Cloudare owns the ERP-to-provider integration layer. Switch providers later and your core integration stays intact.",
  },
  {
    icon: "ShieldCheck",
    title: "Mandate-accredited routes",
    body: "We connect through accredited and authorised providers across India GST, KSA ZATCA, UAE PINT AE, Malaysia LHDN and EU PEPPOL.",
  },
];

export default function PartnersPage() {
  useReveal();
  return (
    <React.Fragment>
      <Seo
        path="/partners"
        title="E-Invoicing & ERP Partners — Vendor-Neutral | Cloudare"
        description="Cloudare integrates with leading GSP, ASP and PDP providers — Avalara, ecosio, Cygnet, Clear, Complyance, BDO, Taxilla, Pagero — and all major ERPs. Vendor-neutral by design."
      />
      <Navbar />

      {/* Hero */}
      <section className="pp-hero reveal">
        <div className="pp-hero__inner">
          <p className="pp-eyebrow">Partners &amp; ecosystem</p>
          <h1 className="pp-hero__title">
            One integration layer. Your choice of provider.
          </h1>
          <p className="pp-hero__subhead">
            Cloudare is vendor-neutral. We integrate your ERP with the GSP, ASP
            or PDP that fits your mandate and stack — and we keep that choice
            yours, not ours.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="pp-section">
        <div className="pp-pillars reveal-stagger">
          {PILLARS.map((p) => (
            <article className="pp-pillar" key={p.title}>
              <div className="pp-pillar__icon">
                <PhosphorIcon name={p.icon} size={24} />
              </div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Tax-tech ecosystem partners */}
      <section className="pp-section pp-section--alt">
        <header className="pp-section__head reveal">
          <p className="pp-eyebrow">Compliance &amp; tax-tech</p>
          <h2>GSP / ASP / PDP providers we integrate</h2>
          <p className="pp-lede">
            We work across the major networks and access points. Tell us your
            preference — or ask us to recommend one for your mandate.
          </p>
        </header>
        <div className="pp-grid reveal-stagger">
          {ECOSYSTEM_PARTNERS.map((p) => (
            <div className="pp-card" key={p.name}>
              <CompanyLogo domain={p.domain} name={p.name} size={64} />
              <p className="pp-card__role">{p.role}</p>
            </div>
          ))}
        </div>
        <p className="pp-disclaimer reveal">
          Logos and names are the property of their respective owners. Listing
          indicates integration capability, not exclusivity or endorsement.
        </p>
      </section>

      {/* ERP platforms */}
      <section className="pp-section">
        <header className="pp-section__head reveal">
          <p className="pp-eyebrow">ERP &amp; source systems</p>
          <h2>ERPs we connect to compliance</h2>
          <p className="pp-lede">
            We build the bridge between your ERP and your e-invoicing provider —
            on Oracle, SAP, Zoho, Dynamics, Odoo and more.
          </p>
        </header>
        <div className="pp-grid pp-grid--erp reveal-stagger">
          {ERP_PLATFORMS.map((p) => (
            <div className="pp-card" key={p.name}>
              <CompanyLogo domain={p.domain} name={p.name} size={64} />
            </div>
          ))}
        </div>
        <div className="pp-links reveal">
          <NavLink to="/gsp-asp-pdp" className="pp-textlink">
            How to choose a GSP / ASP / PDP →
          </NavLink>
          <NavLink to="/services/oracle" className="pp-textlink">
            Oracle ERP services →
          </NavLink>
          <NavLink to="/tax/eu" className="pp-textlink">
            PEPPOL &amp; EU e-invoicing →
          </NavLink>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          eyebrow="Frequently asked questions"
          title="Partners &amp; integrations — FAQ"
          faqs={[
            {
              question: "Is Cloudare tied to a single e-invoicing provider?",
              answer:
                "No. We are vendor-neutral and integrate with Avalara, ecosio, Cygnet, Clear, Complyance, BDO, Taxilla, Pagero and others. We recommend a provider based on your mandate, volumes and ERP — and if you already have a preferred one, we integrate with it.",
            },
            {
              question: "Can you work with a provider that isn't on this list?",
              answer:
                "Yes. The list reflects common integrations, not a limit. If your provider exposes an API or supported exchange format, we can integrate it.",
            },
            {
              question: "Do you resell these providers' licences?",
              answer:
                "Our core role is the ERP-side integration and implementation. Commercial arrangements vary by provider; we'll be transparent about how licensing works for your chosen route.",
            },
            {
              question: "What if I want to switch providers later?",
              answer:
                "Because Cloudare owns the ERP-to-provider integration layer, switching means re-pointing the integration rather than rebuilding it — avoiding lock-in to any single provider.",
            },
          ]}
          emitJsonLd
        />
      </div>

      <ContactCTA
        title="Not sure which provider fits?"
        subtitle="We'll assess your mandate, volumes and ERP, then recommend a provider and own the integration end to end."
        primaryLabel="Book a provider assessment"
      />
      <Footer />
    </React.Fragment>
  );
}
